import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import moment from 'moment'
import axios from '../plugins/axios'
import { checkEmptyValue } from '../plugins/common'
import { useRecoilState } from 'recoil'
import { selectBedInfoState, wardState } from '../plugins/recoil'
import Toast from '../plugins/useToast'
import { useTranslation } from 'react-i18next'

import {ReactComponent as IconCloseBlack} from '../assets/icons/ic-close-black.svg'
import {ReactComponent as IconSearch} from '../assets/icons/ic-search-black.svg'
import './SearchPatientModal.scss'
const SearchPatientModal = forwardRef(({ handleClose, handleSelect }, ref) => {
    const { t } = useTranslation()
    const [searchText, setSearchText] = useState('')
    const [selected, setSelected] = useState(null)
    const [list, setList] = useState([])
    const [selectBedInfo, setSelectBedInfo] = useRecoilState(selectBedInfoState)
    const [curWard, ] = useRecoilState(wardState);
    const isInitiable = () => searchText
    const isSearchable = () => searchText
    const isApliable = () => selected ? true : false
    const isSelected = (item) => {
        if (selected) {
            if (selected.id === item.id) return true
        }
        return false
    }

    const handleSearch = async () => {
        try {
            const res = await axios.get(`/patients?wardId=${curWard.value}&keyword=${searchText}`)
            if (res.code != '200') throw res                                    

            //30일 이내의 데이터만 필터링
            const filteredDaysPatients = res.data.patients.filter(patient => {                
                if(patient.status === 'ADMITTED') return true;                
                const createdAt = moment(patient.updatedAt);
                const thirtyDaysAgo = moment().subtract(30, 'days');
                return createdAt.isAfter(thirtyDaysAgo);
            });

            // 각 환자의 device histories를 조회하여 V20 모델 여부 확인
            const historiesResults = await Promise.all(
                filteredDaysPatients.map(async (patient) => {
                    try {
                        const res = await axios.get(`/patient/${patient.id}/device/histories`);
                        if (res.code != '200') return { id: patient.id, hasV20: false };
                        const hasV20 = res.data.latestHistory.model == 'V20';
                        return { id: patient.id, hasV20 };
                    } catch {
                        return { id: patient.id, hasV20: false };
                    }
                })
            );

            console.log('historiesResults : ', historiesResults);            

            const v20PatientIds = new Set(
                historiesResults.filter(r => r.hasV20).map(r => r.id)
            );

            const finalPatients = filteredDaysPatients.filter(p => !v20PatientIds.has(p.id));

            setList(finalPatients || [])
            //setList(filteredDaysPatients || []) - V20 모델 환자 포함
            setSelected(null)
        } catch (err) { 
            console.warn(err)
            setList([])
            setSelected(null)
        }
    }

    const handleApply = async () => {
        try {
            const res = await axios.get(`/patient/${selected.id}/device/histories`)
            if (res.code != '200') throw res
            if (res.data && res.data.histories.length <= 0) throw res
            handleSelect(selected, res.data.histories)
            handleClose()
        } catch (err) {
            console.warn(err)
            Toast({ isSuccess: false , msg : `There is no device data.` });
        }
    }

    useImperativeHandle(ref, () => ({
        init: () => {
            setSearchText('')
            setList([])
            setSelected(null)
        },
    }));

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 10000 }}>
            <div className="w-[884px] h-[414px] bg-[#DEDEDE]" style={{ left: 500, borderRadius: 8, boxShadow: '0px 12px 12px 0px #00000099', zIndex: 5 }}>            
                <div className="flex items-center bg-[#CECECE]" style={{ padding: '10px 20px', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                    <span className="text-black" style={{ fontSize: 16, fontWeight: 400, lineHeight: '17.6px' }}>{t('modal.patientSearch')}</span>
                    <IconCloseBlack className="ml-auto cursor-pointer" style={{ marginRight: -8 }} onClick={() => { handleClose() }}/>
                </div>
                <div className="flex flex-col gap-[12px]" style={{ padding: 20 }}>
                    <div className="flex">
                        <div className="flex-1 mr-2">
                            <div className="flex items-center bg-white" style={{ padding: '7px 8px', height: 32, borderRadius: 6, border: '1px solid #CECECE', fontWeight: 300 }}>
                                <input type="text" value={searchText} placeholder={t('modal.patientSearchPlaceholder')} onKeyDown={(e) => { if (e.code.indexOf('Enter') !== -1) handleSearch() }} onChange={(e) => { setSearchText(e.target.value) }} style={{ outline: 'none', border: 'none' }} className="text-black w-full"/>
                                <IconSearch className="ml-auto"/>
                            </div>
                        </div>
                        <div className="w-[88px] px-2 flex font-light text-white justify-center items-center bg-gradient-to-b from-[#5E5E5E] to-[#383838]" style={{ fontSize: 16, borderRadius: 6, minHeight: 32, maxHeight: 32, border: '1px solid #5E5E5E', opacity: isSearchable() ? 1 : 0.4, cursor: isSearchable() ? 'pointer' : 'default' }} onClick={() => { handleSearch() }} >{t('modal.search')}</div>
                    </div>
                    <div className="patient-list" style={{ borderRadius: 6, minHeight: 260, maxHeight: 260, fontWeight: 300, fontWeight: '15.4px' }}>
                        <div className="flex items-center text-[#DEDEDE] h-[30px]" style={{ }}>
                            <div className="header-column right-border" style={{ borderTopLeftRadius: 6, minWidth: 180, maxWidth: 180 }}>{t('modal.patientName')}</div>
                            <div className="header-column right-border" style={{ minWidth: 160, maxWidth: 160 }}>{t('modal.patientId')}</div>
                            <div className="header-column right-border" style={{ minWidth: 80, maxWidth: 80 }}>{t('modal.gender')}</div>
                            <div className="header-column right-border" style={{ minWidth: 120, maxWidth: 120 }}>{t('modal.dateOfBirth')}</div>
                            <div className="header-column right-border" style={{ minWidth: 160, maxWidth: 160 }}>{t('modal.status')}</div>
                            <div className="header-column flex-grow" style={{ borderTopRightRadius: 6 }}>{t('modal.discharged')}</div>
                        </div>
                        <div className="scrollbar bg-white" style={{ minHeight: 230, maxHeight: 230, borderBottomLeftRadius: 6, borderBottomRightRadius: 6 }}>
                            {
                                list.map((item, idx) => {
                                    return (
                                        <div onClick={() => { setSelected(item) }} className="flex item-wrapper h-[30px] w-full" style={{ borderBottomWidth: 1, borderColor: '#CECECE', background: isSelected(item) ? 'linear-gradient(180deg, #3182F7 0%, #1562D1 100%)' : '', color: isSelected(item) ? 'white' : 'black' }} key={`${searchText}-${idx}`}>
                                            <div className="item-column right-border truncate" style={{ minWidth: 180, maxWidth: 180 }} title={ item.name }>{ item.name }</div>
                                            <div className="item-column right-border truncate" style={{ minWidth: 160, maxWidth: 160 }} title={ item.devicePatientId }>{ item.devicePatientId }</div>
                                            <div className="item-column right-border truncate" style={{ minWidth: 80, maxWidth: 80 }} title={ item.gender === 'M' ? t('modal.male') : item.gender === 'F' ? t('modal.female') : '-' }>{ item.gender === 'M' ? t('modal.male') : item.gender === 'F' ? t('modal.female') : '-' }</div>
                                            <div className="item-column right-border truncate" style={{ minWidth: 120, maxWidth: 120 }} title={ checkEmptyValue(item.birthDate) }>{ checkEmptyValue(item.birthDate) }</div>
                                            <div className="item-column right-border truncate" style={{ minWidth: 160, maxWidth: 160 }}>{ item.status === 'ADMITTED' ? `${item.bedNumber ? t('modal.admitted', { bedNumber: item.bedNumber }) : ''}` : t('modal.discharged') }</div>
                                            <div className="item-column flex-grow truncate" style={{  }}>{ item.status === 'UNASSIGNED' ? moment(item.updatedAt).format('YYYY-MM-DD') : '-' }</div>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    </div>
                    <div className="flex items-center ml-auto font-light gap-[10px]" style={{ fontSize: 16, lineHeight: '17.6px' }}>
                        <div className="h-[28px] w-[88px] cursor-pointer flex items-center justify-center bg-gradient-to-b from-[#FFFFFF] to-[#DEDEDE]" style={{ borderRadius: 6, border: '1px solid', borderImageSource: `linear-gradient(180deg, #B8B8B8 0%, #B0B0B0 100%)`}} onClick={() => { handleClose() }}>{t('modal.cancel')}</div>
                        <div onClick={() => { handleApply() }} className="text-white h-[28px] w-[88px] flex items-center justify-center"  style={{ cursor: isApliable() ? 'pointer' : 'default', borderRadius: 6, opacity: isApliable() ? 1 : 0.4, background: isApliable() ? 'linear-gradient(180deg, #5E5E5E 0%, #383838 100%)' : '#5E5E5E', border: isApliable() ? '1px solid #5E5E5E' : '' }}>
                            <span className="">{t('modal.ok')}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
})

export default SearchPatientModal